// What the shell tells the pages while they run (decision 064): a login
// completed by the deep link (the token changes under the pages: the
// client is pointed at it again and every query asked anew) or one that
// failed; the session's events as they happen; a transfer's progress
// (F407). Rendered once, under the host alone, around the router.

import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { useHost, type Unlisten } from "./host";
import { syncHostSession } from "./session";
import type { SessionMessage, TransferMessage } from "./types";

export type HostEvents = {
  /** The last deep-link login that failed, until the next one starts. */
  loginError: string | null;
  /** The session's messages, oldest first, the last three hundred. */
  session: SessionMessage[];
  /** The newest session message with its number, counted from the first since the window opened (F444): a reader that handles each message once keeps the number, since the log above is capped. */
  latest: { seq: number; message: SessionMessage } | null;
  /** The transfer under way per airframe; gone once complete. */
  transfers: Record<string, TransferMessage>;
};

const none: HostEvents = { loginError: null, session: [], latest: null, transfers: {} };

const HostEventsContext = createContext<HostEvents>(none);

export function useHostEvents(): HostEvents {
  return useContext(HostEventsContext);
}

export function HostEventsProvider({ children }: { children: ReactNode }) {
  const host = useHost();
  const queryClient = useQueryClient();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [session, setSession] = useState<SessionMessage[]>([]);
  const [latest, setLatest] = useState<HostEvents["latest"]>(null);
  const [transfers, setTransfers] = useState<Record<string, TransferMessage>>({});
  useEffect(() => {
    if (!host) return;
    // A listener that resolves after the cleanup ran is removed at once.
    let cancelled = false;
    const stops: Unlisten[] = [];
    const keep = (p: Promise<Unlisten>) =>
      p.then((stop) => {
        if (cancelled) stop();
        else stops.push(stop);
      });
    keep(
      host.listen("login", async () => {
        setLoginError(null);
        await syncHostSession(host);
        queryClient.invalidateQueries();
      }),
    );
    keep(host.listen<string>("login-error", (e) => setLoginError(e)));
    keep(
      host.listen<SessionMessage>("session", (m) => {
        setSession((log) => [...log.slice(-299), m]);
        setLatest((last) => ({ seq: (last?.seq ?? 0) + 1, message: m }));
        if (m.kind === "started" || m.kind === "watch_started" || m.kind === "done" || m.kind === "failed" || m.kind === "stopped") {
          queryClient.invalidateQueries({ queryKey: ["host"] });
          queryClient.invalidateQueries({ queryKey: ["airframe"] });
          queryClient.invalidateQueries({ queryKey: ["airframes"] });
        }
      }),
    );
    keep(
      host.listen<TransferMessage>("transfer", (t) => {
        setTransfers((all) => {
          const next = { ...all };
          if (t.done >= t.total) delete next[t.hs_uuid];
          else next[t.hs_uuid] = t;
          return next;
        });
      }),
    );
    return () => {
      cancelled = true;
      stops.forEach((stop) => stop());
    };
  }, [host, queryClient]);
  return <HostEventsContext.Provider value={{ loginError, session, latest, transfers }}>{children}</HostEventsContext.Provider>;
}
